/**
 * Shared chat iframe postMessage handling for Office add-ins.
 *
 * This module provides utilities for handling postMessage events from the
 * Walter chat iframe. These are simple string-based events (not RPC) that
 * need to be handled by the parent add-in.
 *
 * Currently handles:
 * - SIGN_OUT_STARTED: User clicked sign out in the chat UI
 *
 * Each add-in implements callbacks to handle events according to its needs.
 */

/**
 * String-based events sent from the chat iframe via postMessage.
 */
export type IframeEvent = "SIGN_OUT_STARTED";

/**
 * Callbacks for handling chat iframe postMessage events.
 * Each add-in implements these to handle events according to its needs.
 */
export interface IframeCallbacks {
  /** Called when the user requests to sign out from the chat UI */
  onSignOut?: () => void | Promise<void>;
}

/**
 * Creates a message event handler for chat iframe postMessage events.
 *
 * Handles origin validation and delegates to the appropriate callback.
 * This is separate from the RPC system and handles simple string events.
 *
 * @param walterHost - The expected origin for messages (e.g., "https://app.getwalter.com")
 * @param callbacks - Handlers for each event type
 * @returns A message event handler function to pass to addEventListener
 *
 * @example
 * ```typescript
 * const handler = createIframeMessageHandler(WALTER_HOST, {
 *   onSignOut: async () => {
 *     await revokeToken();
 *     tokenStorage.clear();
 *     window.location.reload();
 *   },
 * });
 *
 * window.addEventListener("message", handler);
 *
 * // Clean up on unmount
 * window.removeEventListener("message", handler);
 * ```
 */
export function createIframeMessageHandler(
  walterHost: string,
  callbacks: IframeCallbacks,
): (event: MessageEvent) => void {
  return (event: MessageEvent) => {
    // Validate origin - only accept messages from Walter
    if (event.origin !== walterHost) {
      return;
    }

    const data = event.data;

    // Handle SIGN_OUT_STARTED
    if (data === "SIGN_OUT_STARTED") {
      if (callbacks.onSignOut) {
        void callbacks.onSignOut();
      }
      return;
    }

    // Future: Add more chat iframe events here as needed
  };
}
